
/* SHOPIFY STOREFRONT API INTEGRATION — GoodbyePetz
   Products load from Liquid-injected data (window._liquidProducts / window._liquidProduct).
   The Storefront API path below is an optional fallback — it is inactive until a token is configured. */

const SHOPIFY_CONFIG = {
  domain: 'goodbyepetz.myshopify.com',
  token:  '',
  api:    '2024-01'
};

// Maps Shopify product type → site filter category
const SHOPIFY_TYPE_MAP = {
  'necklace':           'necklaces',
  'cremation necklace': 'necklaces',
  'urn necklace':       'necklaces',
  'memorial necklace':  'necklaces',
  'pendant':            'necklaces',
  'locket':             'necklaces',
  'bracelet':           'bracelets',
  'memorial bracelet':  'bracelets',
  'keyring':            'gifts',
  'memorial keyring':   'gifts',
  'keychain':           'gifts',
  'memorial keychain':  'gifts',
  'keepsake':           'everyday',
  'keepsake jewelry':   'everyday',
  'earring':            'everyday',
  'earrings':           'everyday',
};

// ── Static fallback — used only if Liquid + Storefront API both fail ──
const PRODUCTS=[];

// ── Shopify state ──
let shopifyProducts = [];
let shopifyReady    = false;
let shopifyFetched  = false;

// ── Fetch all products from Shopify Storefront API ──
async function fetchShopifyProducts() {
  if (shopifyFetched) return;
  shopifyFetched = true;

  // Skip if not configured
  if (!SHOPIFY_CONFIG.token) {
    console.info('[GoodbyePetz] Storefront API token not configured — relying on Liquid-injected product data.');
    renderShop('all');
    return;
  }

  const query = `{
    products(first: 20) {
      edges {
        node {
          id title handle productType tags
          descriptionHtml
          priceRange { minVariantPrice { amount currencyCode } }
          images(first: 10) { edges { node { url altText } } }
          variants(first: 1) { edges { node { id } } }
        }
      }
    }
  }`;

  try {
    const res = await fetch(
      `https://${SHOPIFY_CONFIG.domain}/api/${SHOPIFY_CONFIG.api}/graphql.json`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': SHOPIFY_CONFIG.token
        },
        body: JSON.stringify({ query })
      }
    );

    if (!res.ok) throw new Error('HTTP ' + res.status);
    const { data, errors } = await res.json();
    if (errors) throw new Error(errors[0].message);

    shopifyProducts = data.products.edges.map(({ node }) => {
      const amt      = parseFloat(node.priceRange.minVariantPrice.amount);
      const currency = node.priceRange.minVariantPrice.currencyCode;
      const sym      = currency === 'USD' ? '$' : currency + ' ';
      const imgs     = node.images.edges.map(e => e.node);
      const varGid   = node.variants.edges[0]?.node.id || '';
      const varId    = varGid.replace('gid://shopify/ProductVariant/', '');
      const tags     = (node.tags || []).map(t => t.toLowerCase());
      const typeKey  = (node.productType || '').toLowerCase();

      const titleLower = node.title.toLowerCase();
      const isCremationType = /cremation|urn|memorial/.test(typeKey);
      let primaryCat;

      if (/earring/.test(titleLower)) {
        primaryCat = 'everyday';
      } else if (/keychain|keyring/.test(titleLower)) {
        primaryCat = 'gifts';
      } else if (/bracelet/.test(titleLower)) {
        primaryCat = isCremationType ? 'bracelets' : 'everyday';
      } else if (/necklace|pendant|locket/.test(titleLower)) {
        primaryCat = isCremationType ? 'necklaces' : 'everyday';
      } else {
        primaryCat = SHOPIFY_TYPE_MAP[typeKey] || 'everyday';
      }
      let cat = [primaryCat];

      return {
        id:          node.id,
        name:        node.title,
        handle:      node.handle,
        type:        node.productType || '',
        price:       'From ' + sym + Math.floor(amt),
        cat:         [...new Set(cat)],
        img:         imgs[0]?.url    || null,
        imgAlt:      imgs[0]?.altText || node.title,
        hoverImg:    imgs[1]?.url    || null,
        allImgs:     imgs.map(n => ({ url: n.url, alt: n.altText || node.title })),
        description: node.descriptionHtml || '',
        variantId:   varId,
        shopifyUrl:  '/products/' + node.handle,
        cartUrl:     varId ? '/cart/' + varId + ':1' : null,
        badge:       tags.includes('bestseller') || tags.includes('best seller') ? 'Best Seller'
                   : tags.includes('new in')     || tags.includes('new')         ? 'New In' : ''
      };
    });

    shopifyReady = true;
    document.getElementById('shopGrid')?.classList.add('shopify-live');
    renderShop('all');

  } catch (err) {
    console.warn('[GoodbyePetz] Shopify load failed:', err.message, '— relying on Liquid-injected product data.');
    const g = document.getElementById('shopGrid');
    if (g) {
      renderShop('all');
    }
  }
}

// ── Render shop grid (Liquid-injected or Storefront API) ──
function renderShop(cat) {
  const g = document.getElementById('shopGrid');
  if (!g) return;

  // Master list is set once (from Liquid injection or Storefront API) so
  // filtering is always applied to the FULL catalog, never a filtered subset.
  if(!window._masterProducts || (shopifyReady && !window._masterFromApi)){
    window._masterProducts = shopifyReady ? shopifyProducts : (window._shopProducts || PRODUCTS);
    window._masterFromApi  = shopifyReady;
  }
  const source = window._masterProducts;
  const list   = cat === 'all' ? source : source.filter(p => p.cat.includes(cat));

  if (!list.length) {
    g.innerHTML = `<div class="shop-error">No pieces found in this category.</div>`;
    return;
  }

  // Store current displayed products for viewProduct() lookup
  window._shopProducts = list;

  g.innerHTML = list.map((p, i) => {
    const isLast = i >= list.length - 2;

    // ── Media ──
    let media;
    if (p.img) {
      const hoverAttrs = p.hoverImg
        ? `data-base="${p.img}" data-hover="${p.hoverImg}"
           onmouseover="this.src=this.dataset.hover"
           onmouseout="this.src=this.dataset.base"`
        : '';
      media = `<div class="prod-media-wrap">
        <img class="prod-img" src="${p.img}" alt="${p.imgAlt}" loading="lazy" ${hoverAttrs}
             onerror="this.style.opacity='0';this.parentNode.classList.add('img-missing')">
        <div class="prod-media-warm"></div>
      </div>`;
    } else {
      media = `<div class="img-ph" style="height:100%;min-height:320px">
        <div class="ph-ico">${p.ico||'◇'}</div>
        <div class="ph-txt">${p.note||p.name}</div>
      </div>`;
    }

    const prodUrl    = p.shopifyUrl || '#';

    return `<div class="product-card" style="${isLast ? 'border-bottom:none' : ''}">
      <a href="${prodUrl}" style="display:contents;cursor:pointer">
        <div class="product-card-media">${media}</div>
        <div class="product-card-overlay"></div>
      </a>
      <div class="product-card-body">
        ${p.badge ? `<span class="prod-badge">${p.badge}</span>` : ''}
        ${p.type  ? `<span class="prod-type">${p.type}</span>`   : ''}
        <h3><a href="${prodUrl}" class="prod-name-link">${p.name}</a></h3>
        ${p.tagline ? `<p class="tagline">${p.tagline}</p>` : ''}
        <div class="product-card-footer">
          <span class="product-price">${p.price}</span>
          <div class="prod-actions">
            <a href="${prodUrl}" class="product-arrow">View Piece <span>→</span></a>
          </div>
        </div>
      </div>
    </div>`;
  }).join('');
}

function showPage(id){
  // If navigating to product page without a selected product, load the first one
  if(id==='product' && !window._currentProduct){
    const source = window._shopProducts || (shopifyReady ? shopifyProducts : PRODUCTS);
    if(source && source.length){ window._currentProduct=source[0]; populatePDP(source[0]); }
  }
  document.querySelectorAll('.page').forEach(p=>p.classList.remove('active'));
  const t=document.getElementById('page-'+id);
  if(t){t.classList.add('active');window.scrollTo({top:0,behavior:'smooth'});}
  document.querySelectorAll('[data-page]').forEach(a=>a.classList.toggle('active-nav',a.dataset.page===id));
  const darkHeroPages=['home','about','gallery'];
  const nav=document.getElementById('mainNav');
  nav.classList.toggle('over-light', !darkHeroPages.includes(id));
  if(id==='shop'){
    if(!shopifyFetched){
      fetchShopifyProducts();
    } else {
      renderShop('all');
    }
  }
  return false;
}

function filterShop(cat,btn){
  document.querySelectorAll('.active-filter').forEach(b=>{b.classList.remove('active-filter');b.style.color='';b.style.borderBottomColor='';});
  btn.classList.add('active-filter');btn.style.color='var(--ink)';btn.style.borderBottomColor='var(--ink)';
  renderShop(cat);
}

function toggleFaq(btn){
  const item=btn.closest('.faq-item');
  const was=item.classList.contains('open');
  item.closest('.faq-list').querySelectorAll('.faq-item').forEach(i=>i.classList.remove('open'));
  if(!was)item.classList.add('open');
}

function switchSlot(n,thumb){
  document.querySelectorAll('.pdp-img-slot').forEach(s=>s.classList.remove('active'));
  document.querySelectorAll('.pdp-gallery-thumb').forEach(t=>t.classList.remove('active'));
  const slot=document.getElementById('pdp-slot-'+n);
  if(slot){slot.classList.add('active');}
  if(thumb){thumb.classList.add('active');}
}
function adjustQty(delta){
  const el=document.getElementById('qty-display');
  if(!el)return;
  let v=parseInt(el.textContent||'1')+delta;
  if(v<1)v=1;
  el.textContent=v;
}

// ── Variant selection (products with multiple variants) ──
function selectVariant(variantId, btn){
  if(window._currentProduct){
    window._currentProduct.variantId = String(variantId);
    var variants = window._currentProduct.variants || [];
    for(var i=0;i<variants.length;i++){
      if(String(variants[i].id)===String(variantId) && variants[i].price){
        var priceEl=document.getElementById('pdp-price');
        if(priceEl) priceEl.textContent=variants[i].price;
        var sp=document.getElementById('sticky-pdp-price');
        if(sp) sp.textContent=variants[i].price;
      }
    }
  }
  if(btn){
    var group=btn.closest('.pdp-variant-group');
    if(group){ group.querySelectorAll('.pdp-variant-btn').forEach(function(b){b.classList.remove('active');}); }
    btn.classList.add('active');
  }
}


// ── Dynamic product selection ──
function viewProduct(idx){
  const source = window._shopProducts || window._liquidProducts ||
                 (typeof shopifyProducts !== 'undefined' ? shopifyProducts : []);
  if(!source) return;
  const product = source[idx];
  if(!product) return;
  const url = product.shopifyUrl || (product.handle ? '/products/'+product.handle : null);
  if(url && url !== '#'){ window.location.href = url; }
}

function populatePDP(p){
  const titleEl = document.getElementById('pdp-title');
  if(titleEl) titleEl.textContent = p.name || '';

  const typeEl = document.getElementById('pdp-type');
  if(typeEl) typeEl.textContent = p.type || 'Memorial Jewelry';

  const priceEl = document.getElementById('pdp-price');
  if(priceEl) priceEl.textContent = p.price || '';

  const descEl = document.getElementById('pdp-desc');
  if(descEl){
    if(p.description && p.description.trim()){
      descEl.innerHTML = p.description;
    } else if(p.tagline){
      descEl.textContent = p.tagline;
    } else {
      descEl.textContent = '';
    }
  }

  const stickyTitle = document.getElementById('sticky-pdp-title');
  const stickyPrice = document.getElementById('sticky-pdp-price');
  if(stickyTitle) stickyTitle.textContent = p.name || '';
  if(stickyPrice) stickyPrice.textContent = p.price || '';

  const ctaNote = document.getElementById('pdp-final-note');
  if(ctaNote) ctaNote.textContent = p.name ? p.name + ' — a love you carry with you.' : 'A meaningful keepsake for the bond that never ends.';

  const qtyEl = document.getElementById('qty-display');
  if(qtyEl) qtyEl.textContent = '1';

  buildPDPGallery(p);
}

function buildPDPGallery(p){
  const mainEl   = document.getElementById('pdp-gallery-main');
  const thumbsEl = document.getElementById('pdp-thumbs');
  if(!mainEl) return;

  const images = [];
  if(p.allImgs && p.allImgs.length){
    p.allImgs.forEach(function(img){ images.push({ url: img.url, alt: img.alt || p.name }); });
  } else if(p.img){
    images.push({ url: p.img, alt: p.imgAlt || p.alt || p.name });
    if(p.hoverImg) images.push({ url: p.hoverImg, alt: p.name });
  }

  if(!images.length){
    mainEl.innerHTML = '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#f5f0e8;border-radius:10px"><span style="font-size:.65rem;letter-spacing:.12em;text-transform:uppercase;color:var(--ink-ghost)">Image coming soon</span></div>';
    if(thumbsEl){ thumbsEl.style.display='none'; }
    return;
  }

  mainEl.innerHTML = images.map(function(img, i){
    return '<div class="pdp-img-slot'+(i===0?' active':'')+'" id="pdp-slot-'+(i+1)+'">'
      +'<img src="'+img.url+'" alt="'+img.alt+'" class="slot-bg" loading="'+(i===0?'eager':'lazy')+'">'
      +'</div>';
  }).join('');

  if(thumbsEl){
    if(images.length < 2){
      thumbsEl.style.display = 'none';
    } else {
      thumbsEl.style.display = 'flex';
      thumbsEl.innerHTML = images.map(function(img, i){
        return '<div class="pdp-gallery-thumb'+(i===0?' active':'')+'" onclick="switchSlot('+(i+1)+',this)">'
          +'<img src="'+img.url+'" alt="'+img.alt+'">'
          +'</div>';
      }).join('');
    }
  }
}

// ── Cart / checkout ──
function addToBag(){
  const p   = window._currentProduct;
  const qty = parseInt(document.getElementById('qty-display')?.textContent||'1')||1;
  if(p && p.variantId){
    window.location.href = '/cart/add?id='+p.variantId+'&quantity='+qty;
  } else if(p && p.shopifyUrl){
    window.location.href = p.shopifyUrl;
  } else {
    showToast('Please select a piece first');
  }
}

function buyItNow(){
  const p   = window._currentProduct;
  const qty = parseInt(document.getElementById('qty-display')?.textContent||'1')||1;
  if(p && p.variantId){
    window.location.href = '/cart/'+p.variantId+':'+qty;
  } else if(p && p.shopifyUrl){
    window.location.href = p.shopifyUrl;
  } else {
    showToast('Redirecting to checkout…');
  }
}



function showToast(msg){
  const t=document.getElementById('toast');
  t.textContent=msg;t.classList.add('show');
  setTimeout(()=>t.classList.remove('show'),2600);
}

// ── Mobile nav functions (hamburger + close) ──
function toggleMobileNav(){
  const mn=document.getElementById('mobileNav');
  const hb=document.getElementById('hamburger');
  if(!mn)return;
  const isOpen=mn.classList.contains('open');
  mn.classList.toggle('open',!isOpen);
  const spans=hb?hb.querySelectorAll('span'):[];
  if(!isOpen){
    if(spans[0])spans[0].style.transform='rotate(45deg) translate(5px,5px)';
    if(spans[1])spans[1].style.opacity='0';
    if(spans[2])spans[2].style.transform='rotate(-45deg) translate(5px,-5px)';
    document.body.style.overflow='hidden';
  } else {
    closeMobileNav();
  }
}

function closeMobileNav(){
  const mn=document.getElementById('mobileNav');
  const hb=document.getElementById('hamburger');
  if(mn)mn.classList.remove('open');
  document.body.style.overflow='';
  const spans=hb?hb.querySelectorAll('span'):[];
  spans.forEach(s=>{s.style.transform='';s.style.opacity='';});
}

// ── Nav scroll handler ──
window.addEventListener('scroll',()=>{
  const nav=document.getElementById('mainNav');
  if(nav)nav.classList.toggle('scrolled',window.scrollY>40);
},{passive:true});

// MOBILE: Touch swipe for PDP image gallery
(function initPDPSwipe(){
  let touchStartX = 0;
  let touchStartY = 0;
  let isSwiping   = false;
  const SWIPE_THRESHOLD = 42; // px

  function currentSlotIndex(){
    const slots = document.querySelectorAll('.pdp-img-slot');
    for(let i=0;i<slots.length;i++){ if(slots[i].classList.contains('active')) return i; }
    return 0;
  }

  function totalSlots(){ return document.querySelectorAll('.pdp-img-slot').length; }

  function goToSlot(n){
    const slots  = document.querySelectorAll('.pdp-img-slot');
    const thumbs = document.querySelectorAll('.pdp-gallery-thumb');
    const dots   = document.querySelectorAll('.pdp-swipe-dot');
    slots.forEach( (s,i)=>s.classList.toggle('active', i===n) );
    thumbs.forEach((t,i)=>t.classList.toggle('active', i===n) );
    dots.forEach(  (d,i)=>d.classList.toggle('active', i===n) );
  }

  function buildDots(){
    const gallery = document.getElementById('pdp-gallery-main');
    if(!gallery) return;
    const old = gallery.parentElement.querySelector('.pdp-swipe-dots');
    if(old) old.remove();
    const total = totalSlots();
    if(total < 2) return;
    const bar = document.createElement('div');
    bar.className = 'pdp-swipe-dots';
    for(let i=0;i<total;i++){
      const d = document.createElement('button');
      d.className = 'pdp-swipe-dot' + (i===0?' active':'');
      d.setAttribute('aria-label','Image '+(i+1));
      d.addEventListener('click',()=>goToSlot(i));
      bar.appendChild(d);
    }
    gallery.insertAdjacentElement('afterend', bar);
  }

  const origBuild = window.buildPDPGallery;
  if(typeof origBuild === 'function'){
    window.buildPDPGallery = function(p){
      origBuild(p);
      setTimeout(buildDots, 60);
    };
  }

  document.addEventListener('touchstart', function(e){
    const gallery = document.getElementById('pdp-gallery-main');
    if(!gallery || !gallery.contains(e.target)) return;
    touchStartX = e.touches[0].clientX;
    touchStartY = e.touches[0].clientY;
    isSwiping = true;
  },{passive:true});

  document.addEventListener('touchend', function(e){
    if(!isSwiping) return;
    isSwiping = false;
    const gallery = document.getElementById('pdp-gallery-main');
    if(!gallery) return;
    const dx = e.changedTouches[0].clientX - touchStartX;
    const dy = e.changedTouches[0].clientY - touchStartY;
    if(Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
    const total = totalSlots();
    if(total < 2) return;
    let idx = currentSlotIndex();
    if(dx < 0) idx = Math.min(idx+1, total-1);
    else        idx = Math.max(idx-1, 0);
    goToSlot(idx);
  },{passive:true});
})();

// MOBILE: Sticky bar Add to Cart wires to addToBag
document.addEventListener('click', function(e){
  if(e.target && e.target.id === 'sticky-add-btn') addToBag();
});

// ── Liquid product normalizers ──
function normalizeLiquidProduct(p){
  return {
    id:p.id, name:p.name||p.title||'', type:p.type||'Memorial Jewelry',
    price:p.price||'', variantId:p.variantId, variants:p.variants||[],
    shopifyUrl:p.shopifyUrl, handle:p.handle,
    description:p.description||'',
    img:p.img||(p.allImgs&&p.allImgs[0]?p.allImgs[0].url:''),
    imgAlt:p.imgAlt||p.name||'', hoverImg:p.hoverImg||null,
    allImgs:p.allImgs||(p.img?[{url:p.img,alt:p.imgAlt||p.name||''}]:[]),
    cat:p.cat||['all']
  };
}
function normalizeLiquidProducts(arr){
  return arr.map(function(p,i){
    var n=normalizeLiquidProduct(p);
    var t=(p.type||'').toLowerCase().trim();
    var tl=(p.name||'').toLowerCase();
    var cr=/cremation|urn|memorial/.test(t);
    var cat;
    if(/earring/.test(tl))               cat='everyday';
    else if(/keychain|keyring/.test(tl))  cat='gifts';
    else if(/bracelet/.test(tl))          cat=cr?'bracelets':'everyday';
    else if(/necklace|pendant|locket/.test(tl)) cat=cr?'necklaces':'everyday';
    else cat=(typeof SHOPIFY_TYPE_MAP!=='undefined'&&SHOPIFY_TYPE_MAP[t])||'everyday';
    n.cat=[cat]; n.idx=i; return n;
  });
}

document.addEventListener('DOMContentLoaded',()=>{
  // ── Collection page ──
  const shopGrid = document.getElementById('shopGrid');
  if(shopGrid){
    if(window._liquidProducts && window._liquidProducts.length){
      window._shopProducts = normalizeLiquidProducts(window._liquidProducts);
      renderShop('all');
    } else {
      fetchShopifyProducts();
    }
  }
  // ── Product page ──
  const pdpGallery = document.getElementById('pdp-gallery-main');
  if(pdpGallery){
    if(window._liquidProduct){
      var prod = normalizeLiquidProduct(window._liquidProduct);
      window._currentProduct = prod;
      populatePDP(prod);
    }
    var st=document.getElementById('sticky-pdp-title');
    var sp=document.getElementById('sticky-pdp-price');
    if(st&&window._currentProduct) st.textContent=window._currentProduct.name;
    if(sp&&window._currentProduct) sp.textContent=window._currentProduct.price;
  }
});
